import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  of,
  firstValueFrom,
} from 'rxjs';
import { catchError, timeout as rxTimeout } from 'rxjs/operators';
import { UserAuthInfo } from '../../models/user-auth-info';
import { LoggingService } from '../logging/logging.service';
import { ConfigService } from '../config.service';
import { TokenStorageService } from './token-storage.service';
import { Token } from './token.model';
import { PendingRequest } from './pending-request.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  /* ------------------------------------------------------------------ */
  /*                          CONFIG CONSTANTS                          */
  /* ------------------------------------------------------------------ */
  private readonly REFRESH_TIMEOUT_MS = 30000;  // 30 seconds

  /* ------------------------------------------------------------------ */
  /*                     INTERNAL STATE & STREAMS                       */
  /* ------------------------------------------------------------------ */
  private readonly userAuthInfoSubject =
    new BehaviorSubject<UserAuthInfo | null>(null);
  /** public stream for components */
  public readonly userAuthInfo$ = this.userAuthInfoSubject.asObservable();

  /** Current token instance (self-aware) */
  private currentToken: Token | null = null;

  /** Pending refresh request tracker */
  private pendingRefresh: PendingRequest<void> | null = null;

  /** Timer for scheduled token refresh */
  private refreshTimer: number | null = null;

  /** track if the service has been initialized (singleton pattern) */
  private static initialized = false;

  constructor(
    private readonly tokenStorage: TokenStorageService,
    private readonly log: LoggingService,
    private readonly configService: ConfigService
  ) {
    // Removed loadAuthStateFromCookies() from constructor
    this.log.info('AuthService', 'Constructor called - auth state not loaded yet');
    this.setupVisibilityListener();
  }

  /* ------------------------------------------------------------------ */
  /*                     SERVICE INITIALIZATION                         */
  /* ------------------------------------------------------------------ */
  initialize(): void {
    /** Load auth state from cookies and schedule token refresh if needed */

    // Singleton pattern - prevent duplicate initialization
    if (AuthService.initialized) {
      this.log.warn('AuthService', 'Duplicate initialization detected for AuthService - skipping');
      return;
    }

    AuthService.initialized = true;
    this.log.info('AuthService', 'initialize() called - first time initialization');

    this.loadAuthStateFromCookies();

    // FIXED: Check if we just came from OAuth redirect
    // If so, don't auto-refresh - the token is brand new!
    const urlParams = new URLSearchParams(window.location.search);
    const hasTokenParam = urlParams.has('token');
    const isOAuthRedirect = hasTokenParam || urlParams.has('authError');

    if (isOAuthRedirect) {
      this.log.info('AuthService', 'OAuth redirect detected, skipping auto-refresh (token is fresh)');
      return;
    }

    // Check if user is already authenticated
    if (this.currentToken) {
      const debugInfo = this.currentToken.getDebugInfo();
      this.log.info('AuthService', 'Token state on initialization:', debugInfo);

      if (this.currentToken.isExpired()) {
        this.log.info('AuthService', 'Token is expired, will refresh on next request');
      } else if (this.currentToken.needsRefresh()) {
        this.log.info('AuthService', 'Token approaching expiry, will refresh soon');
      } else {
        this.log.info('AuthService', 'Token is still valid, no refresh needed');
      }
    } else {
      this.log.info('AuthService', 'User not authenticated');
    }

    this.log.info('AuthService', 'Auth service initialized');
  }

  /* ------------------------------------------------------------------ */
  /*                     TOKEN / REFRESH PUBLIC API                     */
  /* ------------------------------------------------------------------ */
  /**
   * Get valid token, refreshing if necessary.
   *
   * @throws Error if no valid token available and refresh fails
   */
  async getValidAccessToken(): Promise<string | null> {
    // No token at all
    if (!this.currentToken) {
      this.log.warn('AuthService', 'No token available - user must log in');
      return null;
    }

    // Token expired - must refresh
    if (this.currentToken.isExpired()) {
      this.log.info('AuthService', 'Token expired, refreshing now');
      await this.refresh();

      if (!this.currentToken) {
        this.log.error('AuthService', 'Token refresh failed');
        return null;
      }
    }

    // Token expiring soon - trigger background refresh (don't wait)
    if (this.currentToken.needsRefresh()) {
      this.log.info('AuthService', 'Token approaching expiry, triggering background refresh');
      this.refresh().catch(err => {
        this.log.error('AuthService', 'Background refresh failed:', err);
      });
    }

    return this.currentToken.getValue();
  }

  /**
   * Refresh token using refresh token cookie.
   * Handles concurrent refresh requests.
   */
  private async refresh(): Promise<void> {
    // If refresh in progress, wait for it
    if (this.pendingRefresh) {
      if (this.pendingRefresh.isCancelled()) {
        // Previous refresh was cancelled, clear it
        this.pendingRefresh = null;
      } else {
        // Wait for in-progress refresh
        try {
          await this.pendingRefresh.promise;
          return;
        } catch (error) {
          // In-progress refresh failed, will start new one below
          this.pendingRefresh = null;
        }
      }
    }

    // Start new refresh
    const refreshPromise = this.performTokenRefresh();
    this.pendingRefresh = new PendingRequest(refreshPromise, this.REFRESH_TIMEOUT_MS);

    try {
      await refreshPromise;
    } finally {
      this.pendingRefresh = null;
    }
  }

  /**
   * Actual token refresh implementation.
   */
  private async performTokenRefresh(): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.REFRESH_TIMEOUT_MS);

    try {
      const config = this.configService.getConfig();

      if (!config.tokenRefreshUrl) {
        throw new Error('Token refresh URL not configured');
      }

      const response = await fetch(config.tokenRefreshUrl, {
        method: 'POST',
        credentials: 'include', // Send refresh token cookie
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.token) {
        throw new Error('Invalid refresh response: missing access token');
      }

      this.setToken(data.token);

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Token refresh timed out');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Set new token and schedule automatic refresh.
   */
  private setToken(tokenString: string): void {
    try {
      this.currentToken = new Token(tokenString);
      this.scheduleRefresh();

      // Update user auth info with new token
      const userInfo = this.getUserAuthInfo();
      if (userInfo) {
        this.setUserAuthInfo({ ...userInfo, token: tokenString }, { silent: true });
      }
    } catch (error) {
      this.log.error('AuthService', 'Invalid token:', error);
      this.clearAuthState();
    }
  }

  /**
   * Schedule automatic token refresh.
   * Uses wall-clock time from Token object.
   */
  private scheduleRefresh(): void {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (!this.currentToken) {
      return;
    }

    const msUntilRefresh = this.currentToken.getTimeUntilRefresh();

    if (msUntilRefresh <= 0) {
      // Token already needs refresh - do it now
      this.refresh().catch(err => {
        this.log.error('AuthService', 'Immediate refresh failed:', err);
        this.handleRefreshFailure(err);
      });
    } else {
      // Schedule refresh for later
      this.log.info('AuthService', `Scheduling token refresh in ${Math.round(msUntilRefresh / 1000)}s`);
      this.refreshTimer = window.setTimeout(() => {
        this.refresh().catch(err => {
          this.log.error('AuthService', 'Scheduled refresh failed:', err);
          this.handleRefreshFailure(err);
        });
      }, msUntilRefresh);
    }
  }

  /**
   * CRITICAL: Handle page visibility changes for mobile browsers.
   *
   * Mobile browsers suspend all timers and network requests when tabs
   * are backgrounded. When the user returns, we must:
   * 1. Cancel any stale pending refresh requests
   * 2. Immediately check if token is still valid
   * 3. Refresh if needed
   */
  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.handleVisibilityChange();
      }
    });
  }

  /**
   * Handle page becoming visible (user returned from background).
   */
  private async handleVisibilityChange(): Promise<void> {
    this.log.info('AuthService', 'Page became visible, checking token state');

    // Cancel stale pending refresh
    if (this.pendingRefresh?.isStale()) {
      this.log.warn(
        'AuthService',
        `Cancelling stale refresh request (age: ${this.pendingRefresh.getAge()}ms)`
      );
      this.pendingRefresh.cancel();
      this.pendingRefresh = null;
    }

    // Check token state
    if (!this.currentToken) {
      this.log.info('AuthService', 'No token on visibility change');
      return;
    }

    const debugInfo = this.currentToken.getDebugInfo();
    this.log.info('AuthService', 'Token state on visibility change:', debugInfo);

    // Token expired or expiring soon - refresh immediately
    if (this.currentToken.isExpired() || this.currentToken.needsRefresh()) {
      this.log.info('AuthService', 'Token needs refresh, starting now');

      try {
        await this.refresh();
      } catch (error) {
        this.log.error('AuthService', 'Token refresh failed after visibility change:', error);
        this.handleRefreshFailure(error);
      }
    }
  }

  /**
   * Handle token refresh failure.
   * Clears auth state and redirects to login.
   */
  private handleRefreshFailure(error: any): void {
    this.log.error('AuthService', 'Token refresh failed, clearing auth state:', error);
    this.clearAuthState();
  }

  /* ------------------------------------------------------------------ */
  /*                              LOGOUT                                */
  /* ------------------------------------------------------------------ */
  logout(): void {
    // Get the configuration for logout URL
    let logoutUrl = 'api/auth/logout'; // Default fallback
    try {
      const config = this.configService.getConfig();
      if (config && config.logoutUrl) {
        logoutUrl = config.logoutUrl;
      }
    } catch (e) {
      this.log.warn('AuthService', 'Error getting config for logout, using default path', e);
    }

    // Call the logout endpoint to clean up refresh tokens on the server
    try {
      fetch(logoutUrl, {
        method: 'POST',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json'
        }
      }).catch(err => {
        // Silent fail - we're cleaning up
        this.log.debug('AuthService', 'Error during logout call:', err);
      });
    } catch (e) {
      this.log.debug('AuthService', 'Error initiating logout call:', e);
    }

    this.clearAuthState();
    this.log.info('AuthService', 'User logged-out');
  }

  /* ------------------------------------------------------------------ */
  /*                      COOKIE / STATE MANAGEMENT                     */
  /* ------------------------------------------------------------------ */
  setUserAuthInfo(info: UserAuthInfo | null, options: { silent?: boolean } = {}): void {
    this.log.info('AuthService', `setUserAuthInfo called with: ${info ? 'User Info' : 'NULL'}, silent: ${options.silent || false}`);
    if (info) {
      console.log('[AuthService] setUserAuthInfo received fullName:', info.fullName);
      this.log.info('AuthService', `Setting auth info - Email: ${info.email}, Token: ${info.token ? 'Present (length: ' + info.token.length + ')' : 'Missing'}`);
    }

    if (!info) {
      this.log.warn('AuthService', 'Clearing auth state due to null info');
      this.clearAuthState();
      return;
    }

    // Store user info in token storage
    this.tokenStorage.setUserInfo({
      givenName: info.givenName,
      fullName: info.fullName,
      email: info.email,
      profilePictureUrl: info.profilePictureUrl
    });

    // Store token if it's not a placeholder
    this.tokenStorage.setAuthToken(info.token, 7);

    // Always update the internal state
    (this.userAuthInfoSubject as any)._value = info;

    // Only notify subscribers if not silent
    if (!options.silent) {
      this.userAuthInfoSubject.next(info);
      this.log.info('AuthService', 'Auth info published to subscribers');
    } else {
      this.log.info('AuthService', 'Silent auth update - subscribers not notified');
    }
  }

  /**
   * Get raw token - may be expired!
   * Use getValidAccessToken() for auto-refresh behavior.
   */
  getAuthToken(): string | null {
    return this.tokenStorage.getAuthToken();
  }

  /** Get user profile picture URL */
  getUserProfilePictureUrl(): string {
    return this.tokenStorage.getProfilePictureUrl();
  }

  getUserAuthInfo(): UserAuthInfo | null {
    return this.userAuthInfoSubject.getValue();
  }

  /** Reload auth state from cookies - useful after token exchange */
  reloadAuthStateFromCookies(): void {
    this.log.info('AuthService', 'reloadAuthStateFromCookies() called');
    this.loadAuthStateFromCookies();
  }

  /* ------------------------------------------------------------------ */
  /*                         INTERNAL  HELPERS                          */
  /* ------------------------------------------------------------------ */

  /**
   * Load authentication state from storage.
   * FAIL-FAST: Throws error if auth cookies exist but no token found (impossible state).
   */
  private loadAuthStateFromCookies(): void {
    this.log.info('AuthService', 'loadAuthStateFromCookies() called');

    // Get token via TokenStorageService (single source of truth)
    const token = this.tokenStorage.getAuthToken();
    this.log.info('AuthService', `Token from cookie: ${token ? 'Present (length: ' + token.length + ')' : 'Missing'}`);

    // If we have auth cookies but no ws_token, automatically refresh to get new ws_token
    // This handles the scenario where user returns after idle period and cookies rotated
    if (!token && this.tokenStorage.hasAuthCookies()) {
      this.log.warn('AuthService', 'Auth cookies exist but no ws_token found - attempting automatic token refresh');

      this.refresh().then(() => {
        if (this.currentToken) {
          this.log.info('AuthService', 'Automatic token refresh successful - reloading auth state');
          this.loadAuthStateFromCookies(); // Retry loading state with new ws_token
        } else {
          this.log.warn('AuthService', 'Automatic token refresh failed - clearing stale cookies');
          this.tokenStorage.clearAll();
          this.userAuthInfoSubject.next(null);
        }
      }).catch(err => {
        this.log.error('AuthService', 'Error during automatic token refresh', err);
        this.tokenStorage.clearAll();
        this.userAuthInfoSubject.next(null);
      });

      return; // Return early - will be called again after refresh completes
    }

    if (!token) {
      this.log.warn('AuthService', 'No token found in cookies, setting auth state to null');
      this.userAuthInfoSubject.next(null);
      return;
    }

    // Get user info from token storage
    const userInfo = this.tokenStorage.getUserInfo();
    const info: UserAuthInfo = {
      ...userInfo,
      token: token,
    };

    console.log('[AuthService] loadAuthStateFromCookies - fullName from storage:', userInfo.fullName);
    console.log('[AuthService] loadAuthStateFromCookies - givenName from storage:', userInfo.givenName);
    this.log.info('AuthService', `Loaded auth state from cookies - Email: ${info.email}, Token: ${info.token ? 'Present' : 'Missing'}`);
    this.userAuthInfoSubject.next(info);
    this.log.info('AuthService', `Loaded auth state for user: ${info.email}`);

    // Create Token instance and schedule refresh
    try {
      this.currentToken = new Token(info.token);
      this.scheduleRefresh();
    } catch (error) {
      this.log.error('AuthService', 'Invalid token from storage:', error);
      this.clearAuthState();
    }
  }

  private clearAuthState(): void {
    this.currentToken = null;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.pendingRefresh) {
      this.pendingRefresh.cancel();
      this.pendingRefresh = null;
    }

    this.tokenStorage.clearAll();
    this.userAuthInfoSubject.next(null);
  }

  /**
   * Determine whether the token is *already* expired or will expire within `skewMs`
   */
  private isTokenExpired(token: string, skewMs = 0): boolean {
    if (!token) return true;

    // Handle HttpOnly cookie placeholders
    if (token === 'secure' || token === 'httponly') {
      // We can't check expiry for HttpOnly tokens
      // Trust that the backend will handle expiry
      return false;
    }

    try {
      const [, payload] = token.split('.');
      // Convert URL-safe Base64 to standard Base64
      const standardBase64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      // Add padding if needed
      const paddedBase64 = standardBase64 + '='.repeat((4 - standardBase64.length % 4) % 4);
      const data = JSON.parse(atob(paddedBase64));
      const expMs = data.exp * 1000;
      return Date.now() + skewMs >= expMs;
    } catch (e) {
      this.log.error('AuthService', 'Failed decoding token:', e);
      return true;
    }
  }

  /**
   * Decode token to extract claims
   */
  private decodeToken(token: string): any {
    if (!token || token === 'secure' || token === 'httponly') {
      return null;
    }

    try {
      const [, payload] = token.split('.');
      const standardBase64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = standardBase64 + '='.repeat((4 - standardBase64.length % 4) % 4);
      return JSON.parse(atob(paddedBase64));
    } catch (e) {
      this.log.error('AuthService', 'Failed decoding token:', e);
      return null;
    }
  }


  /* ------------------------------------------------------------------ */
  /*     COOKIE METHODS REMOVED - USE TokenStorageService INSTEAD      */
  /* ------------------------------------------------------------------ */
  // All cookie operations now handled by TokenStorageService
  // This ensures single source of truth and prevents bugs like
  // calling getCookie('token') instead of getAuthToken()
}
